// client/src/pages/admin/Page_DripEditor/Page_DripEditor.js
//
// Drip M3.3 - Editor de la secuencia de nurture (free -> pago).
//
// Controla desde admin: interruptor de negocio, % de la oferta y, por paso,
// dia de envio / asunto / activacion. El envio real necesita ADEMAS el gate de
// infraestructura (env), que aqui se muestra como informacion de solo lectura.

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Switch,
  InputNumber,
  Input,
  Table,
  Button,
  Alert,
  Tag,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useAuth } from "../../../hooks";
import { dripApi } from "../../../api/dripApi";
import { PageHeader } from "../../../ui";

const { Text } = Typography;

const STEP_LABELS = {
  hype: "Bienvenida (hype)",
  activacion: "Activacion",
  oferta: "Oferta",
};

// `embedded`: renderizado dentro de la pestana "Correos funnel" del panel
// comercial (/admin/leads?tab=drip) — sin padding propio ni PageHeader.
export function Page_DripEditor({ embedded = false }) {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(false);
  const [offerPct, setOfferPct] = useState("30");
  const [steps, setSteps] = useState([]);
  const [envGate, setEnvGate] = useState(false);
  const [offerCode, setOfferCode] = useState("");
  const [updatedBy, setUpdatedBy] = useState(null);

  const [paidFunnel, setPaidFunnel] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const res = await dripApi.getSequence(accessToken);
      const seq = res?.data?.sequence || {};
      setActive(!!seq.active);
      setOfferPct(String(seq.offer_pct ?? "30"));
      setSteps((seq.steps || []).map((s) => ({ ...s })));
      setEnvGate(!!res?.data?.envGate);
      setOfferCode(res?.data?.offerCode || "");
      setUpdatedBy(seq.updated_by || null);
      // Funnel post-compra (solo lectura). No bloqueante si falla.
      try {
        const paid = await dripApi.getPaidSequence(accessToken);
        setPaidFunnel(paid?.data || null);
      } catch (_e) {
        setPaidFunnel(null);
      }
    } catch (err) {
      message.error(err.message || "Error cargando la secuencia");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStep = (idx, patch) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const patch = {
        active,
        offer_pct: offerPct,
        steps: steps.map((s) => ({
          step: s.step,
          dayOffset: s.dayOffset,
          subject: s.subject || null,
          active: s.active,
        })),
      };
      const res = await dripApi.patchSequence(accessToken, patch);
      const seq = res?.data?.sequence || {};
      setActive(!!seq.active);
      setOfferPct(String(seq.offer_pct ?? offerPct));
      setSteps((seq.steps || []).map((s) => ({ ...s })));
      setUpdatedBy(seq.updated_by || null);
      message.success("Secuencia guardada");
    } catch (err) {
      message.error(err.message || "Error guardando");
    } finally {
      setSaving(false);
    }
  }, [accessToken, active, offerPct, steps]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns = [
    { title: "Paso", dataIndex: "step", key: "step", width: 60 },
    {
      title: "Plantilla",
      dataIndex: "key",
      key: "key",
      width: 160,
      render: (k) => STEP_LABELS[k] || k,
    },
    {
      title: "Dia (desde alta)",
      dataIndex: "dayOffset",
      key: "dayOffset",
      width: 150,
      render: (v, r, i) => (
        <InputNumber
          min={0}
          max={14}
          value={v}
          onChange={(val) => updateStep(i, { dayOffset: val })}
        />
      ),
    },
    {
      title: "Asunto (opcional)",
      dataIndex: "subject",
      key: "subject",
      render: (v, r, i) => (
        <Input
          placeholder="Usar el asunto de la plantilla"
          value={v || ""}
          onChange={(e) => updateStep(i, { subject: e.target.value })}
          maxLength={200}
        />
      ),
    },
    {
      title: "Activo",
      dataIndex: "active",
      key: "active",
      width: 80,
      render: (v, r, i) => (
        <Switch
          checked={!!v}
          onChange={(val) => updateStep(i, { active: val })}
        />
      ),
    },
  ];

  return (
    <div style={embedded ? { maxWidth: 980 } : { padding: 24, maxWidth: 980 }}>
      {!embedded && (
        <PageHeader
          title="Drip de correos"
          subtitle="Secuencia de nurture que convierte cuentas free en cuentas de pago"
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Dos interruptores para enviar"
        description="El envio real necesita los DOS encendidos: el de infraestructura (variable de entorno, lo activa el equipo tecnico al ir a produccion) y el de negocio (este panel). Aqui controlas la secuencia; nada se envia hasta que la infraestructura este ON."
      />

      <Card title="Estado" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch checked={active} onChange={setActive} />
            <Text strong>Secuencia activa (negocio)</Text>
          </div>
          <div>
            Infraestructura (envio real):{" "}
            <Tag color={envGate ? "green" : "default"}>
              {envGate ? "ON" : "OFF"}
            </Tag>
            {!envGate && (
              <Text type="secondary">
                {" "}
                — mientras este OFF, el cron no envia aunque la secuencia este
                activa
              </Text>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Text>Oferta del correo final:</Text>
            <InputNumber
              min={0}
              max={100}
              value={Number(offerPct)}
              onChange={(v) => setOfferPct(String(v ?? 0))}
              addonAfter="%"
            />
            <Text type="secondary">
              Cupon: <b>{offerCode}</b> (ligado a Stripe, se cambia en M4)
            </Text>
          </div>
        </Space>
      </Card>

      <Card
        title="Pasos de la secuencia"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={columns}
          dataSource={steps}
          rowKey="step"
          size="small"
          pagination={false}
        />
      </Card>

      {paidFunnel && (
        <Card
          title={
            <Space>
              Funnel de activacion post-compra
              <Tag color="green">SIEMPRE ACTIVO</Tag>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Correos de uso para compradores de plan"
            description={paidFunnel.audience}
          />
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => `paid-${r.step ?? "0"}`}
            dataSource={[
              {
                step: 0,
                dia: "Al pagar",
                label: paidFunnel.day0?.label,
                subject: paidFunnel.day0?.subject,
              },
              ...(paidFunnel.steps || []).map((s) => ({
                step: s.step,
                dia: `D+${s.dayOffset}`,
                label: s.label,
                subject: s.subject,
              })),
            ]}
            columns={[
              { title: "Paso", dataIndex: "step", width: 60 },
              { title: "Dia", dataIndex: "dia", width: 90 },
              { title: "Objetivo", dataIndex: "label" },
              { title: "Asunto", dataIndex: "subject" },
            ]}
          />
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            Los pasos viven en codigo (paidOnboardingCron) y se envian en el
            mismo barrido diario de las 09:00. Idempotente por comprador: nunca
            se duplica un paso.
          </Text>
        </Card>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary">
          {updatedBy ? `Ultima edicion: ${updatedBy}` : ""}
        </Text>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
